import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { CentsDataGridServer } from 'components/common/dataGrid/dataGridServer';
import TableSearchBar from 'components/common/search/TableSearchBar';
import { DEFAULT_PAGE_LIMIT } from 'utils/constants';
import { getInventoryOrders } from 'api/admin/orders/orders';

const COLUMNS = [
  { headerName: 'Store Name', field: 'storeName', sortable: false, flex: 1 },
  { headerName: 'Business Name', field: 'businessName', sortable: false, flex: 1 },
  { headerName: 'Customer Name', field: 'customerName', sortable: false, flex: 1 },
  { headerName: 'Order Number', field: 'orderCode', sortable: false, flex: 1 },
  { headerName: 'Order ID', field: 'id', sortable: false, flex: 1 },
  { headerName: 'Status', field: 'status', sortable: false, flex: 1 },
  { headerName: 'Order Total', field: 'orderTotal', sortable: false, flex: 1 },
  { headerName: 'Order creation date', field: 'createdAt', sortable: false, flex: 1 },
  { headerName: 'Payment Date', field: 'updatedAt', sortable: false, flex: 1 },
  { headerName: 'Payment Method', field: 'paymentMethod', sortable: false, flex: 1 },
  { headerName: 'Payment Status', field: 'paymentStatus', sortable: false, flex: 1 },
  { headerName: 'Tip Amount', field: 'tipAmount', sortable: false, flex: 1 },
  { headerName: 'Credit Amount', field: 'creditAmount', sortable: false, flex: 1 },
  { headerName: 'Promotion Amount', field: 'promotionAmount', sortable: false, flex: 1 },
];

const InventoryOrdersTable = () => {
  const history = useHistory();
  const [searchString, setSearchString] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [rowCount, setRowCount] = useState(0);

  const { data, isLoading } = useQuery(
    ['searchInventoryOrders', { searchString, pageNumber }],
    async () => {
      const { data } = await getInventoryOrders(pageNumber, searchString);

      /**
       * due to performance issues, our API to fetch inventory orders now only returns 20 service orders as the total
       * however, we want to make sure we can click to the next page of inventory orders in datagrid
       *
       * so here, we're tricking datagrid into thinking there are more inventory orders to fetch by modifying the rowCount
       *
       * if the returned total is 20, that means there are likely more results to fetch and the API returned the max
       * in that case, we take 20, multiply it by the page number + 1 (since page number starts at 0), and add 1,
       * making the total rowCount of 21.
       *
       * if the total is not 20, then we know we've reached the end of the results, and we set the rowCount to the total
       */
      const adjustedPaginationTotal =
        data.total === DEFAULT_PAGE_LIMIT ? (pageNumber + 1) * DEFAULT_PAGE_LIMIT + 1 : data.total;
      setRowCount(adjustedPaginationTotal);

      return {
        inventoryOrders: data.inventoryOrders,
        inventoryOrderCount: data.total,
      };
    },
    {
      onError: (error) => {
        // maintaining existing error handling behavior
        console.log(error);
        Sentry.captureException(error);
      },
    },
  );
  const { inventoryOrders } = data ?? {};

  const handleSearch = (searchInput) => {
    if (searchInput.length > 0) {
      if (searchInput !== searchString) {
        setPageNumber(0);
      }
      setSearchString(searchInput);
    } else {
      setPageNumber(0);
      setSearchString('');
    }
  };

  const handleRowClick = ({ id }) => {
    history.push(`/inventory-orders/${Number(id)}`);
  };

  const handlePageChange = ({ page }) => {
    setPageNumber(page);
  };

  return (
    <div className="page-container">
      <h1 className="inventory-orders-header">Inventory Orders</h1>
      <div className="inventory-orders-search-bar">
        <TableSearchBar
          value={searchString}
          placeholder="Find an inventory order"
          onSearch={handleSearch}
        />
      </div>
      <CentsDataGridServer
        tableName="INVENTORY_ORDERS"
        columns={COLUMNS}
        data={inventoryOrders ?? []}
        rowCount={rowCount}
        pageSize={DEFAULT_PAGE_LIMIT}
        page={pageNumber}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        isLoading={isLoading}
      />
    </div>
  );
};

export default InventoryOrdersTable;
